import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { queryKeys } from "@/lib/query-keys";
import { openingStockService } from "@/services/opening-stock";

import type {
  CreateOpeningStockReq,
  ListOpeningStocksParams,
  UpdateOpeningStockReq,
} from "@artisancode/api-types";

export function useOpeningStocks(params?: ListOpeningStocksParams) {
  return useQuery({
    queryKey: queryKeys.openingStocks.list(params as Record<string, unknown>),
    queryFn: () => openingStockService.list(params),
  });
}

export function useOpeningStock(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.openingStocks.detail(id ?? ""),
    queryFn: () => openingStockService.detail(id as string),
    enabled: Boolean(id && id !== "new"),
  });
}

export function useCreateOpeningStock() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      body,
      idempotencyKey,
    }: {
      body: CreateOpeningStockReq;
      idempotencyKey?: string;
    }) => openingStockService.create(body, idempotencyKey),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.openingStocks.all });
    },
  });
}

export function useUpdateOpeningStock() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & UpdateOpeningStockReq) =>
      openingStockService.update(id, body),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: queryKeys.openingStocks.all });
      qc.setQueryData(queryKeys.openingStocks.detail(data.id), data);
    },
  });
}

export function useDeleteOpeningStock() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => openingStockService.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.openingStocks.all });
    },
  });
}

export function useCompleteOpeningStock() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => openingStockService.complete(id),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: queryKeys.openingStocks.all });
      qc.setQueryData(queryKeys.openingStocks.detail(data.id), data);
      qc.invalidateQueries({ queryKey: queryKeys.stocks.all });
      qc.invalidateQueries({ queryKey: queryKeys.rawMaterials.all });
      qc.invalidateQueries({ queryKey: queryKeys.products.all });
    },
  });
}
